import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Color, Typography, BoxBorder } from '@genial/design-tokens';
import { Box } from 'native-base';
import React, { useEffect } from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import {
  TableContentItemCaptions,
  TableContentItemText,
} from '../../../../../../../../../components/TableContent';
import { useFixedIncomeAssetController } from '../../../../../hooks/products/FixedIncome';
import { LateralBar } from '../../../components/LateralBar';
import {
  FeeInfo,
  MaturityInfo,
  NetValueInfo,
  ProfitabilityInfo,
  RiskInfo,
  TaxInfo,
  UnitPriceInfo,
} from '../shared';

import { FixedIncomeItemSelectedTypes } from '.';

export const FixedIncomeItemSelectedTablet: React.FC<FixedIncomeItemSelectedTypes.Props> = ({
  offer,
  onUpdate,
}) => {
  const id = offer.productIdNormalized;
  const {
    control,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    helpText,
    helpTextProps,
    isFocused,
    showMessageAdjustUnitPrice,
    currentValueFormatted,
    currentValue,
    ableToChangeFees,
    selectedFee,
    selectedFeeId,
    setSelectedFeeId,
    minApplication,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  } = useFixedIncomeAssetController(offer);

  useEffect(() => {
    if (offer.orderValue === currentValue || !currentValue) {
      return;
    }
    const updatedOffer = {
      ...offer,
      orderValue: currentValue || 0,
      estimatedIncomeCalculated,
      estimatedNetValueCalculated,
    };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  useEffect(() => {
    if (offer.orderFee?.feeId === selectedFee?.feeId || !selectedFee) {
      return;
    }
    const updatedOffer = {
      ...offer,
      orderFee: selectedFee,
      estimatedIncomeCalculated,
      estimatedNetValueCalculated,
    };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFee]);

  const infosProps = {
    offer,
    selectedFee,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  };

  return (
    <Box p="inline.xxs">
      <LateralBar
        backgroundColor={Color['product.rf']}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <VStack flex={1} space="inline.xxxs">
        <HStack flex={1} justifyContent="space-between">
          <UI.ListItem testId="selected-product-name" stackProps={{ w: '1/5' }}>
            <VStack maxW="100%" pr="inline.xxxs" space="stack.quarck">
              <TableContentItemText variant="semibold" color="neutral.black" noOfLines={1}>
                {offer.productDisplayNormalized}
              </TableContentItemText>
              <TableContentItemCaptions icons={offer.captions} forceDesktop />
            </VStack>
          </UI.ListItem>
          <RiskInfo w="1/5" {...infosProps} />
          <TaxInfo w="1/5" {...infosProps} />
          <ProfitabilityInfo w="1/5" {...infosProps} />
        </HStack>
        <HStack flex={1} justifyContent="space-between">
          <MaturityInfo w="1/5" {...infosProps} />
          <Stack w="1/5">
            <VStack space="stack.quarck" justifyContent="space-between">
              <HStack space="inline.quarck" alignItems="center">
                <UI.Text variant="semibold">Taxa</UI.Text>
                <UI.Text fontSize="xs">(Spread)</UI.Text>
              </HStack>
              <Stack bgColor="backgroundshade" borderRadius={BoxBorder.thick} w="full">
                <UI.Select
                  triggerProps={{
                    isDisabled: !ableToChangeFees,
                    _disabled: { opacity: 0.2 },
                  }}
                  testID="fee-select"
                  options={offer.distributionFeesNormalized.map((feeOption) => ({
                    label: feeOption.label,
                    value: feeOption.feeId.toString(),
                  }))}
                  placeholder="--"
                  selectedValue={selectedFeeId?.toString() || ''}
                  onValueChange={(value) => setSelectedFeeId(Number(value))}
                />
              </Stack>
            </VStack>
          </Stack>
          <UnitPriceInfo w="1/5" {...infosProps} />
          <FeeInfo w="1/5" {...infosProps} />
        </HStack>
        <HStack flex={1} justifyContent="space-between">
          <UI.ListItem
            testId="selected-product-input"
            title="Valor a aplicar"
            stackProps={{ flex: 1, pr: 'inline.xxxs' }}>
            <HStack w="full" space="inline.xxxs" alignItems="end">
              <Stack flex={1}>
                <InputMoney
                  id={`fixedIncome-input${id}`}
                  name="createAppliedAmount"
                  control={control}
                  handleFocus={handleInputFocus}
                  handleBlur={handleInputBlur}
                  handleSubmit={handleSubmit}
                  handleClear={handleInputClear}
                  currentValue={currentValueFormatted}
                  hasFocus={isFocused}
                  minValue={minApplication}
                  customInputProps={{
                    ...{
                      ...(showMessageAdjustUnitPrice && {
                        helpTextProps,
                        helpText,
                      }),
                    },
                    ...{ ...(isFocused && hasError && { error: hasError }) },
                    ...{ ...(isFocused && !hasError && { helpText }) },
                  }}
                />
                {showMessageAdjustUnitPrice && !isFocused && (
                  <TableContentItemText fontSize={Typography['fontsize.xs']} color="neutral.light">
                    Ajustado por preço unitário
                  </TableContentItemText>
                )}
              </Stack>
            </HStack>
          </UI.ListItem>
          <NetValueInfo w="1/5" {...infosProps} />
        </HStack>
      </VStack>
    </Box>
  );
};
